import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useUserCapabilities } from '#pplx/frontend/apps/ask/queries/user-queries/useUserCapabilities.js';
import { useSearchModeForSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useSearchModeForSearchModel.js';
import { useMaxUpsell } from '#pplx/frontend/apps/ask/subscription/hooks/useMaxUpsell.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { getIsBestSearchModel, SearchMode, SearchModel, } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { useCallback } from 'react';
/**
 * Provides a callback function that determines whether or not the user is
 * allowed to select a search model.
 *
 * @returns A callback function that determines if the user can select a search
 * model.
 */
export function useCanSelectSearchModel() {
    const { gpt4Limit, pplxAlphaLimit, modelSpecificLimits } = useThreadConfig();
    const { specialCapabilities } = useUserCapabilities();
    const isUserLoggedIn = useIsUserLoggedIn();
    const { hasAccessToProFeatures, isMax } = useIsSubscribed();
    const shouldRenderMaxUpsell = useMaxUpsell();
    const { getModelConfig } = useSearchModels({ reason: 'use_can_select_search_model' });
    const hasReachedProLimits = !specialCapabilities.unlimitedProSearch && !gpt4Limit.available && !hasAccessToProFeatures;
    const hasReachedAlphaLimit = !specialCapabilities.unlimitedResearch && (!gpt4Limit.available || !pplxAlphaLimit.available);
    const getSearchModeForSearchModel = useSearchModeForSearchModel();
    return useCallback((searchModel) => {
        const searchMode = getSearchModeForSearchModel(searchModel);
        const modelConfig = getModelConfig(searchModel);
        const isSelectingModel = searchMode === SearchMode.SEARCH &&
            // If the user selects a specific model
            !getIsBestSearchModel(searchModel);
        // The Default search model is accessible to everyone
        if (searchModel === SearchModel.DEFAULT) {
            return true;
        }
        // Free users cannot select anything but the default search model,
        // except when a special capability explicitly allows PRO for visitors.
        // Paid users who have reached their beta limit cannot access the beta model
        if (!isUserLoggedIn) {
            // Visitors with unlimited Pro capability can select Pro (and any Pro sub-model)
            const canSelectPro = specialCapabilities.unlimitedProSearch &&
                getSearchModeForSearchModel(searchModel) === SearchMode.SEARCH &&
                !shouldRenderMaxUpsell(searchMode);
            // Visitors with unlimited Research capability can select Research
            const canSelectResearch = specialCapabilities.unlimitedResearch &&
                getSearchModeForSearchModel(searchModel) === SearchMode.RESEARCH &&
                !shouldRenderMaxUpsell(searchMode);
            return canSelectPro || canSelectResearch;
        }
        // Study mode is available to everyone
        if (getSearchModeForSearchModel(searchModel) === SearchMode.STUDY) {
            return true;
        }
        // Model council (AGENTIC_RESEARCH) — only MAX users and special capability users can select
        // from the normal model selector. Pro users access it via related query suggestions instead.
        if (getSearchModeForSearchModel(searchModel) === SearchMode.AGENTIC_RESEARCH) {
            if (!isMax && !specialCapabilities.maxModelSelection) {
                return false;
            }
        }
        // ASI model preference selection is tier-gated separately from Computer execution access.
        // Org-level Computer disablement is enforced at submit time by useComputerBeforeSubmit.
        if (getSearchModeForSearchModel(searchModel) === SearchMode.ASI) {
            if (!hasAccessToProFeatures &&
                !specialCapabilities.unlimitedProSearch &&
                !specialCapabilities.maxModelSelection) {
                return false;
            }
        }
        // Max users can select any model except study mode (which requires eppo flag + student status)
        if (isMax) {
            return true;
        }
        // For non max users enforce model specific limits which can grant access to specific max models
        if (modelConfig?.subscription_tier === 'max') {
            const modelSpecificLimit = modelSpecificLimits[searchModel] ?? 0;
            const hasReachedModelSpecificLimit = modelSpecificLimit <= 0;
            if (hasReachedModelSpecificLimit) {
                return false;
            }
        }
        if (hasReachedProLimits) {
            return false;
        }
        if (getSearchModeForSearchModel(searchModel) === SearchMode.RESEARCH &&
            hasReachedAlphaLimit) {
            return false;
        }
        if (shouldRenderMaxUpsell(searchMode)) {
            return false;
        }
        if (!hasAccessToProFeatures && isSelectingModel && !specialCapabilities.unlimitedProSearch) {
            return false;
        }
        return true;
    }, [
        hasReachedProLimits,
        hasReachedAlphaLimit,
        hasAccessToProFeatures,
        specialCapabilities.unlimitedProSearch,
        specialCapabilities.unlimitedResearch,
        specialCapabilities.maxModelSelection,
        isUserLoggedIn,
        shouldRenderMaxUpsell,
        isMax,
        getModelConfig,
        modelSpecificLimits,
        getSearchModeForSearchModel,
    ]);
}
//# sourceMappingURL=useCanSelectSearchModel.js.map